import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, AlertCircle, Loader, Gift, BadgeCheck, CalendarCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = '/api';

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/');
      return;
    }
    axios.get(`${API}/payment/status/${sessionId}`, { headers: getAuthHeaders() })
      .then((response) => setResult(response.data))
      .catch(() => setResult({ payment_status: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Zahlung wird überprüft...</p>
        </div>
      </div>
    );
  }

  const paid = result?.payment_status === 'paid';
  const kind = result?.kind;
  const details = result?.details || {};
  const content = kind === 'membership'
    ? {
        icon: BadgeCheck,
        title: 'Mitgliedschaft ist aktiv!',
        text: `${details.plan_name || 'Ihr Abo'} kann ab sofort bei der Terminbuchung verwendet werden.`,
        action: 'Zu Abos & Gutscheinen',
        href: '/angebote',
      }
    : kind === 'gift_card'
      ? {
          icon: Gift,
          title: 'Gutschein ist bereit!',
          text: 'Der Gutschein wurde erstellt und per E-Mail verschickt.',
          action: 'Meine Gutscheine',
          href: '/angebote',
        }
      : {
          icon: CalendarCheck,
          title: 'Termin ist bestätigt!',
          text: kind === 'booking_full'
            ? 'Der Termin wurde vollständig mit Karte bezahlt. Ihre Buchungsbestätigung wird per E-Mail gesendet.'
            : 'Die Zahlung war erfolgreich. Ihre Buchungsbestätigung wird per E-Mail gesendet.',
          action: 'Meine Termine',
          href: '/profile?tab=appointments',
        };
  const Icon = content.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
          paid ? 'bg-green-100' : 'bg-red-100'
        }`}>
          {paid
            ? <Icon className="w-12 h-12 text-green-600" />
            : <AlertCircle className="w-12 h-12 text-red-600" />}
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 mb-3">
          {paid ? content.title : 'Zahlung nicht abgeschlossen'}
        </h1>
        <p className="text-slate-600 mb-6">
          {paid ? content.text : 'Es wurde nichts bestätigt. Bitte starten Sie den Vorgang erneut.'}
        </p>

        {paid && kind === 'booking_deposit' && (
          <div className="rounded-xl bg-blue-50 p-4 mb-6 text-left text-sm text-slate-700">
            <div className="flex justify-between"><span>Anzahlung</span><strong>{Number(details.deposit_amount || 10).toFixed(2).replace('.', ',')} €</strong></div>
            <div className="flex justify-between mt-2"><span>Restbetrag im Salon</span><strong>{Number(details.remaining_amount || 0).toFixed(2).replace('.', ',')} €</strong></div>
          </div>
        )}

        {paid && kind === 'booking_full' && (
          <div className="rounded-xl bg-green-50 p-4 mb-6 text-left text-sm text-slate-700">
            <div className="flex justify-between">
              <span>Online bezahlt</span>
              <strong>{Number(details.paid_amount || 0).toFixed(2).replace('.', ',')} €</strong>
            </div>
            <div className="flex justify-between mt-2">
              <span>Restbetrag im Salon</span>
              <strong>0,00 €</strong>
            </div>
          </div>
        )}

        {paid && kind === 'gift_card' && details.code && (
          <div className="rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 p-5 mb-6">
            <p className="text-sm text-slate-600">Gutscheincode</p>
            <p className="text-2xl font-bold tracking-wider text-blue-700 my-2">{details.code}</p>
            <p className="font-semibold">{Number(details.amount || 0).toFixed(2).replace('.', ',')} €</p>
          </div>
        )}

        <Button onClick={() => navigate(paid ? content.href : '/angebote')} className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
          {paid ? content.action : 'Erneut versuchen'}
        </Button>
        {paid && content.href !== '/' && (
          <Button onClick={() => navigate('/')} variant="ghost" className="w-full rounded-full mt-2">
            Zur Startseite
          </Button>
        )}
      </div>
    </div>
  );
};

export default BookingSuccessPage;
